import { Alert, Button, Card, Col, Row, Skeleton, Tooltip } from 'antd';
import message from '../../../utils/messageWithScroll';
import { ButtonFilter } from 'components/ButtonFilter';
import NumberItem from 'components/NumberItem';
import { LanguageContext } from 'contexts/LanguageContext';
import { NumberConsumer } from 'contexts/legacy/NumberContext';
import { Number } from 'interfaces/number';
import React, { useContext, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import AddEditNumberView from './AddEditNumberView';
import VirtualNumber from './VirtualNumber';
import CrossSellDashboard from 'components/CrossSellBanners/CrossSellDashboard';

interface NumberProViewProps {
	number: Number;
}

function NumberProView({ number }: NumberProViewProps) {
	const { dictionary } = useContext(LanguageContext);
	const [isVisible, setIsVisible] = useState(false);
	const [isActiveNumber, setIsActiveNumber] = useState(true);
	const [isModalVisible, setIsModalVisible] = useState(false);

	function handleShowActiveNumbers() {
		setIsActiveNumber(true);
	}

	function handleShowInactiveNumbers() {
		setIsActiveNumber(false);
	}

	function handleAddShow() {
		if (number.creationProLimit || number.proTrialAvailable) {
			setIsVisible(true);
		} else {
			message.warning(dictionary.plan_limit);
		}
	}

	function handleCancel() {
		setIsVisible(false);
	}

	function handleOk() {
		setIsVisible(false);
	}

	function showModal() {
		setIsModalVisible(true);
	}

	function handleCancelModal() {
		setIsModalVisible(false);
	}

	return (
		<>
			{isVisible && (
				<AddEditNumberView
					visible={isVisible}
					preSelectedAPI="zapi"
					title={
						<FormattedMessage
							id={number.proTrialAvailable ? 'create_pro_trial' : 'add_pro_numbers_title'}
						/>
					}
					cancelAction={handleCancel}
					okAction={handleOk}
				/>
			)}
			{isModalVisible && (
				<VirtualNumber visible={isModalVisible} cancelAction={handleCancelModal} />
			)}
			<Card
				title={<FormattedMessage id="pro_numbers_title" />}
				style={{ width: 'auto', margin: 5 }}
				extra={
					<FormattedMessage
						id="amount_of_numbers_pro"
						values={{
							active_pro: number.proNumbers.actives.length + number.proNumbers.inactives.length,
							total_pro: number.max_numbers_pro,
						}}
					/>
				}
			>
				<Row>
					<Col span={12}>
						<Skeleton loading={number.loadingNumber}>
							<ButtonFilter
								name={dictionary.actives}
								active={isActiveNumber}
								count={number.proNumbers.actives.length ? number.proNumbers.actives.length : 0}
								onClick={handleShowActiveNumbers}
							/>
							<ButtonFilter
								name={dictionary.inactives}
								active={!isActiveNumber}
								count={number.proNumbers.inactives.length ? number.proNumbers.inactives.length : 0}
								onClick={handleShowInactiveNumbers}
							/>
						</Skeleton>
					</Col>
					<Col span={12} style={{ display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
						<Button
							style={{
								borderTop: 0,
								borderLeft: 0,
								borderRight: 0,
								borderRadius: 0,
								borderWidth: 3,
							}}
							onClick={handleAddShow}
						>
							<FormattedMessage id={'add_pro_numbers_title'} />
						</Button>
					</Col>
				</Row>
				<Row style={{ marginTop: 10 }}>
					<Skeleton loading={number.loadingNumber}>
						{isActiveNumber ? (
							<>
								{' '}
								{number.proNumbers.actives.length > 0 ? (
									<>
										<div
											style={{
												backgroundColor: '#e8f9fe',
												padding: 12,
												borderBottom: '1px solid #fff',
											}}
										>
											<Row>
												<Col
													span={6}
													style={{
														lineHeight: '25px',
														fontWeight: 'bold',
													}}
												>
													<FormattedMessage id="numbers_form_nickname" />
												</Col>
												<Col
													span={5}
													style={{
														lineHeight: '25px',
														fontWeight: 'bold',
													}}
												>
													<FormattedMessage id="number" />
												</Col>
												<Col
													span={4}
													style={{
														lineHeight: '25px',
														fontWeight: 'bold',
													}}
												>
													<FormattedMessage id="subscription" />
												</Col>

												<Col
													span={3}
													style={{
														lineHeight: '25px',
														fontWeight: 'bold',
														textAlign: 'center',
													}}
												>
													<FormattedMessage id="api_connected" />
												</Col>

												<Col
													span={4}
													style={{
														lineHeight: '25px',
														fontWeight: 'bold',
														textAlign: 'center',
													}}
												>
													<div
														style={{
															display: 'flex',
															alignItems: 'center',
															justifyContent: 'center',
														}}
													>
														<Tooltip title={<FormattedMessage id="audio_transcription_tooltip" />}>
															<span style={{ cursor: 'help' }}>
																<FormattedMessage id="audio_transcription_column" />
																<sup
																	style={{
																		fontSize: '8px',
																		color: '#1890ff',
																		fontWeight: 'bold',
																		marginLeft: '-2px',
																		verticalAlign: 'super',
																		position: 'relative',
																		top: '-2px',
																	}}
																>
																	<FormattedMessage id="new" />
																</sup>
															</span>
														</Tooltip>
													</div>
												</Col>

												<Col
													span={2}
													style={{
														lineHeight: '25px',
														fontWeight: 'bold',
														textAlign: 'right',
													}}
												>
													<FormattedMessage id="actions" />
												</Col>
											</Row>
										</div>
										{number.proNumbers.actives.map((number) => (
											<NumberItem
												key={number.api + number.id}
												numberInfo={number}
												numberType={'pro'}
											/>
										))}
									</>
								) : (
									<Alert
										message={<FormattedMessage id="not_found_registers" />}
										type="warning"
										showIcon
									/>
								)}
							</>
						) : (
							<>
								{' '}
								{number.proNumbers.inactives.length > 0 ? (
									<>
										<div
											style={{
												backgroundColor: '#e8f9fe',
												padding: 12,
												borderBottom: '1px solid #fff',
											}}
										>
											<Row>
												<Col
													span={6}
													style={{
														lineHeight: '25px',
														fontWeight: 'bold',
													}}
												>
													<FormattedMessage id="numbers_form_nickname" />
												</Col>
												<Col
													span={5}
													style={{
														lineHeight: '25px',
														fontWeight: 'bold',
													}}
												>
													<FormattedMessage id="number" />
												</Col>
												<Col
													span={4}
													style={{
														lineHeight: '25px',
														fontWeight: 'bold',
													}}
												>
													<FormattedMessage id="subscription" />
												</Col>

												<Col
													span={3}
													style={{
														lineHeight: '25px',
														fontWeight: 'bold',
														textAlign: 'center',
													}}
												>
													<FormattedMessage id="api_connected" />
												</Col>

												<Col
													span={4}
													style={{
														lineHeight: '25px',
														fontWeight: 'bold',
														textAlign: 'center',
													}}
												>
													<div
														style={{
															display: 'flex',
															alignItems: 'center',
															justifyContent: 'center',
														}}
													>
														<Tooltip title={<FormattedMessage id="audio_transcription_tooltip" />}>
															<span>
																<FormattedMessage id="audio_transcription_column" />
																<sup
																	style={{
																		fontSize: '8px',
																		color: '#1890ff',
																		fontWeight: 'bold',
																		marginLeft: '-2px',
																		verticalAlign: 'super',
																		position: 'relative',
																		top: '-2px',
																	}}
																>
																	<FormattedMessage id="new" />
																</sup>
															</span>
														</Tooltip>
													</div>
												</Col>

												<Col
													span={2}
													style={{
														lineHeight: '25px',
														fontWeight: 'bold',
														textAlign: 'right',
													}}
												>
													<FormattedMessage id="actions" />
												</Col>
											</Row>
										</div>
										{number.proNumbers.inactives.map((number) => (
											<NumberItem
												key={number.api + number.id}
												numberInfo={number}
												numberType={'pro'}
											/>
										))}
									</>
								) : (
									<Alert
										message={<FormattedMessage id="not_found_registers" />}
										type="warning"
										showIcon
									/>
								)}
							</>
						)}
					</Skeleton>
				</Row>

				<Row>
					<CrossSellDashboard />
				</Row>
			</Card>
		</>
	);
}

export default NumberConsumer(NumberProView);
